import { Box } from "@mui/material"
import { nanoid } from "@reduxjs/toolkit"
import { ReactNode, useCallback, useContext, useState } from "react"
import { APIError } from "../../app/appTypes"
import { useAppDispatch, useAppSelector } from "../../app/hooks"
import { store } from "../../app/store"
import { JustComments, TextWithComments, TiptapComment, TiptapPos } from "../../app/TextWithComments"
import { impossible, parseISODate } from "../../app/Utils"
import { showError } from "../notifications/notificationsSlice"
import { CommentStatus } from "../urs/URSCommentSlice"
import { changeDocCommentStatus, createComment, deleteDocComment, DocComment, updateComment } from "./commentsAPI"
import { DocumentSectionHeader } from "./components/DocumentSectionHeader"
import { SectionDeleteContext } from "./DocumentContent"
import { updateDocSection } from "./documentsApi"
import { blocksSelector, commentsSelectors, isDocCommentable, isDocCommentResolvable, isDocEditable, sectionSelectors, selectDocument } from "./documentSlice"
import { SectionContentLoader } from "./SectionContent"
import { DocumentSectionDeleteDialog } from "./utils/DeleteSectionDialog"

function nop():Promise<void> {
    return new Promise(() => {})
}

function mapStatus(input: DocComment): CommentStatus {
    switch (input.status) {
        case 'NEW':
            return 'N'
        case 'CLOSED':
            return 'R'
        case 'RESOLVED':
            return 'R'
        case 'WONTFIX':
            return 'U'
        default:
            return 'N'
    }
}

function commentToTiptapComment(c: DocComment): TiptapComment {
    return {
        id: c.id,
        content: c.comment,
        author: c.createdBy,
        authorFullName: c.createdByFullName,
        created: parseISODate(c.createDate),
        new: false,
        pos: c.pos,
        quot: '',
        externalId: c.id,
        status: mapStatus(c),
        replays: c.replies.map(r => commentToTiptapComment(r)),
    }
}

interface SectionCommentsProps {
    sectionId: string
    component?: ReactNode | undefined
    contextType: string
    contextGuid: string | null
}
export const SectionComments = (props: SectionCommentsProps) => {
    const { header } = useAppSelector(selectDocument)?.doc ?? impossible()
    const dispatch = useAppDispatch()
    const { sectionId, component, contextType, contextGuid } = props
    const { type: sectionType } = useAppSelector(state => sectionSelectors.selectById(state, sectionId)) ?? impossible()
    const section = useAppSelector(state => sectionSelectors.selectById(state, sectionId)) ?? impossible()
    const { numeration } = section
    // const content = useAppSelector(state => blocksSelector.selectById(state, sectionId))?.description ?? ''
    const comments = useAppSelector(state => commentsSelectors.selectAll(state).filter(x => x.sectionGuid === sectionId && (contextGuid === null || x.contextGuid === contextGuid)))
    const isCommentable = useAppSelector(isDocCommentable)
    const isResolvable = useAppSelector(isDocCommentResolvable)
    const isEditable = useAppSelector(isDocEditable)

    const deleteSection = useContext(SectionDeleteContext)

    const [error, setError] = useState<APIError | null>(null)
    const [del, setDel] = useState(false)
    const [deleting, setDeleting] = useState(false)

    const handleCreateComment = useCallback(async (content: string, pos: TiptapPos | null, parentCommentId: string | null) => {
        const created = await dispatch(createComment({
            clientId: nanoid(),
            comment: content,
            documentId: header.id,
            pos,
            parentCommentId,
            context: '',
            sectionGuid: sectionId,
            sectionType,
            type: 'INTERNAL',
            contextType,
            contextGuid,
        })).unwrap()
        return created.id
    }, [sectionId, sectionType, contextType, contextGuid])

    const handleUpdateComment = useCallback(async (id: string, content: string) => {
        const c = commentsSelectors.selectById(store.getState(), id) ?? impossible()
        dispatch(updateComment({
            ...c,
            comment: content,
        }))
    }, [])

    const handleDeleteComment = useCallback(async (id: string) => {
        const c = commentsSelectors.selectById(store.getState(), id) ?? impossible()
        dispatch(deleteDocComment(c))
    }, [])

    const handleResolveComment = useCallback(async (id: string) => {
        dispatch(changeDocCommentStatus({
            id,
            documentId: header.id,
            status: 'RESOLVED',
        }))
    }, [])

    const handleUnresolveComment = useCallback(async (id: string) => {
        dispatch(changeDocCommentStatus({
            id,
            documentId: header.id,
            status: 'WONTFIX',
        }))
    }, [])

    const handleApprove = async (newName: string) => {
        if (newName) {
            try {
                await dispatch(updateDocSection({ ...section, name: newName })).unwrap()
            } catch (error) {
                setError(error as APIError)
            }
        }
    };

    const handleDeleteClick = () => setDel(true)

    const handleConfirmDeleteClick = async () => {
        try {
            setDeleting(true)
            deleteSection(sectionId)
        } catch (error) {
            dispatch(showError('error'))
        } finally {
            setDeleting(false)
        }
    }
    const handleCancelDelClick = () => setDel(false)

    if (component) return <Box sx={{
        display: 'flex',
        flexDirection: 'row',
        flexWrap: 'wrap',
        alignItems: 'flex-start',
        gap: 1,
        [`& > div:nth-child(1)`]: {
            flex: '1 1 0%',
            minWidth: 0,
        },
        [`& > div:nth-child(2)`]: {
            flex: '0 0 auto',
            width: { xs: '100%', lg: '400px' },
            maxWidth: '100%',
        },
    }}>
        <>{component}</>
        <JustComments
            sectionId={sectionId}
            comments={comments.map(x => commentToTiptapComment(x))}
            requestComment={nop}
            createComment={handleCreateComment}
            updateComment={handleUpdateComment}
            deleteComment={handleDeleteComment}
            resolveComment={handleResolveComment}
            unresolveComment={handleUnresolveComment}
            reply={nop}
            isSendingEnabled={isCommentable}
            isResolvingEnabled={isResolvable}
        />
    </Box>

    return <SectionContentLoader sectionId={sectionId}>
        <DocumentSectionHeader
            numeration={numeration}
            name={section.name}
            isEditable={isEditable}
            error={error}
            onSaveName={handleApprove}
            onDeleteClick={handleDeleteClick}
        />
        {/* keyed so every section gets its own editor, and with it its own undo history */}
        <TextWithComments
            key={sectionId}
            sectionId={sectionId}
            comments={comments.map(x => commentToTiptapComment(x))}
            requestComment={nop}
            createComment={handleCreateComment}
            updateComment={handleUpdateComment}
            deleteComment={handleDeleteComment}
            resolveComment={handleResolveComment}
            unresolveComment={handleUnresolveComment}
            reply={nop}
            isSendingEnabled={isCommentable}
            isResolvingEnabled={isResolvable}
        />
        {del &&
            <DocumentSectionDeleteDialog
                deleting={deleting}
                hasSubSections={section.subSectionsIds.length > 0}
                onConfirm={handleConfirmDeleteClick}
                onCancel={handleCancelDelClick}
            />
        }
    </SectionContentLoader>
}
